import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Music, LogIn, LogOut, Info, FileText, Shield, Upload, Mic, Activity, User, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, signIn, signOut, isAdmin, isProUser, isBetaUser, getDisplayName, getPublicHandle } = useAuth();
  const { showToast } = useToast();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // public_name (profile table) -> email, never the Cognito pool name.
  const displayName = getDisplayName();
  const initial = (displayName[0] || "?").toUpperCase();
  const publicHandle = getPublicHandle();

  // Determine badge display based on user plan
  const isPro = isProUser();
  const isBeta = isBetaUser();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b transition-colors"
      style={{
        height: '54px',
        backgroundColor: 'rgba(10, 10, 10, 0.8)',
        borderColor: 'var(--border-color)'
      }}
    >
      <div className="max-w-7xl mx-auto h-full px-4 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-soundwave-500 to-soundwave-600 flex items-center justify-center shadow-lg">
              <Music className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
              PasteWaves
            </span>
          </button>
          {/* Plan Badge - Gold for Pro, Orange for Beta, Nothing for Free */}
          {isPro && (
            <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-amber-500/20 text-amber-400 border border-amber-500/30 shadow-sm">
              PRO
            </span>
          )}
          {isBeta && !isPro && (
            <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-soundwave-500/20 text-soundwave-500 border border-soundwave-500/30">
              BETA
            </span>
          )}

        </div>

        {/* Right side actions - Auth UI */}
        <div className="flex items-center gap-2 sm:gap-3">
          {!isAuthenticated ? (
            <motion.button
              onClick={signIn}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-soundwave-500 hover:bg-soundwave-600 text-white text-sm font-medium transition-all shadow-md hover:shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <LogIn className="w-4 h-4" />
              <span>Sign In</span>
            </motion.button>
          ) : (
            <>
              {/* Record Audio Button - Orange to match recording theme */}
              <motion.button
                onClick={() => navigate('/record')}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-soundwave-500/10 transition-colors text-soundwave-500"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Record Audio"
              >
                <Mic className="w-5 h-5" />
                <span className="hidden md:inline text-sm font-medium">Record</span>
              </motion.button>

              {/* Upload Button - Gradient with icon only on mobile */}
              <motion.button
                onClick={() => navigate('/upload')}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-soundwave-500 to-neon-500 hover:from-soundwave-600 hover:to-neon-600 text-white font-medium transition-all shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Upload"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Upload</span>
              </motion.button>

              {/* User Menu Dropdown */}
              <div className="relative" ref={menuRef}>
                <motion.button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-soundwave-600 to-soundwave-700 flex items-center justify-center shadow-md">
                    <span className="text-white font-bold text-xs">{initial}</span>
                  </div>
                  <span className="hidden lg:inline text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    {displayName}
                  </span>
                </motion.button>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {showUserMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-56 rounded-xl border shadow-2xl backdrop-blur-md overflow-hidden"
                      style={{
                        backgroundColor: 'rgba(10, 10, 10, 0.95)',
                        borderColor: 'var(--border-color)'
                      }}
                    >
                      {/* User Info Header */}
                      <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
                        <p className="font-semibold text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                          {displayName}
                        </p>
                        {user?.email && (
                          <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                            {user.email}
                          </p>
                        )}
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        {publicHandle && (
                          <button
                            onClick={() => {
                              navigate(`/@${publicHandle}`);
                              setShowUserMenu(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors text-left"
                          >
                            <Globe className="w-4 h-4 text-soundwave-500" />
                            <span className="text-sm" style={{ color: 'var(--text-primary)' }}>View public profile</span>
                          </button>
                        )}
                        <button
                          onClick={() => {
                            navigate('/profile');
                            setShowUserMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors text-left"
                        >
                          <User className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                          <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Profile</span>
                        </button>
                        <button
                          onClick={() => {
                            showToast('Coming soon! 🚀', 'info');
                            setShowUserMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors text-left"
                        >
                          <Info className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                          <span className="text-sm" style={{ color: 'var(--text-primary)' }}>About</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/terms');
                            setShowUserMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors text-left"
                        >
                          <FileText className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                          <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Terms of Service</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/privacy');
                            setShowUserMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors text-left"
                        >
                          <Shield className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                          <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Privacy Policy</span>
                        </button>
                      </div>

                      {/* Admin Status - Only visible to admins */}
                      {isAdmin() && (
                        <div className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                          <button
                            onClick={() => {
                              navigate('/admin/status');
                              setShowUserMenu(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-soundwave-500/10 transition-colors text-left"
                          >
                            <Activity className="w-4 h-4 text-soundwave-500" />
                            <span className="text-sm text-soundwave-500 font-medium">Admin Status</span>
                          </button>
                        </div>
                      )}

                      {/* Sign Out */}
                      <div className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                        <button
                          onClick={() => {
                            signOut();
                            setShowUserMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-500/10 transition-colors text-left"
                        >
                          <LogOut className="w-4 h-4 text-red-500" />
                          <span className="text-sm text-red-500 font-medium">Sign Out</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;